/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { audioContext } from "./utils";
import AudioRecordingWorklet from "./worklets/audio-processing";
import VolMeterWorket from "./worklets/vol-meter";

import { createWorketFromSrc } from "./audioworklet-registry";
import EventEmitter from "eventemitter3";

function arrayBufferToBase64(buffer: ArrayBuffer) {
  var binary = "";
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: MediaStreamAudioSourceNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  vuWorklet: AudioWorkletNode | undefined;
  // The actual sample rate used by the AudioContext (may differ from requested on iOS/Safari)
  effectiveSampleRate: number = 16000;

  private starting: Promise<void> | null = null;

  constructor(public sampleRate = 16000) {
    super();
  }

  async start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Could not request user media");
    }

    this.starting = new Promise(async (resolve, reject) => {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioContext = await audioContext({ sampleRate: this.sampleRate });
        // Record the actual sample rate chosen by the browser
        this.effectiveSampleRate = this.audioContext.sampleRate;
        this.source = this.audioContext.createMediaStreamSource(this.stream);

        if (this.audioContext.audioWorklet) {
          // Preferred path: AudioWorklet
          const workletName = "audio-recorder-worklet";
          const src = createWorketFromSrc(workletName, AudioRecordingWorklet);

          await this.audioContext.audioWorklet.addModule(src);
          this.recordingWorklet = new AudioWorkletNode(
            this.audioContext,
            workletName,
          );

          this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
            // worklet processes recording floats and messages converted buffer
            const arrayBuffer = ev.data.data.int16arrayBuffer;

            if (arrayBuffer) {
              const arrayBufferString = arrayBufferToBase64(arrayBuffer);
              this.emit("data", arrayBufferString);
            }
          };
          this.source.connect(this.recordingWorklet);

          // vu meter worklet
          const vuWorkletName = "vu-meter";
          await this.audioContext.audioWorklet.addModule(
            createWorketFromSrc(vuWorkletName, VolMeterWorket),
          );
          this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
          this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
            this.emit("volume", ev.data.volume);
          };

          this.source.connect(this.vuWorklet);
        } else {
          // Fallback path: ScriptProcessorNode (for older mobile browsers)
          const bufferSize = 2048;
          const scriptNode = (this.audioContext as any).createScriptProcessor
            ? (this.audioContext as any).createScriptProcessor(bufferSize, 1, 1)
            : (this.audioContext as any).createJavaScriptNode(bufferSize, 1, 1);

          scriptNode.onaudioprocess = (audioProcessingEvent: AudioProcessingEvent) => {
            const inputBuffer = audioProcessingEvent.inputBuffer;
            const channelData = inputBuffer.getChannelData(0);
            const length = channelData.length;

            // Volume (RMS) for simple VU meter
            let sumSquares = 0;
            for (let i = 0; i < length; i++) {
              const s = channelData[i];
              sumSquares += s * s;
            }
            const rms = Math.sqrt(sumSquares / length);
            this.emit("volume", rms);

            // Convert float32 [-1,1] to PCM16
            const int16Buffer = new Int16Array(length);
            for (let i = 0; i < length; i++) {
              let s = channelData[i];
              s = Math.max(-1, Math.min(1, s));
              int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            const arrayBufferString = arrayBufferToBase64(int16Buffer.buffer);
            this.emit("data", arrayBufferString);
          };

          // Route through a silent gain to avoid echoing to speakers on some mobile browsers
          const silentGain = this.audioContext.createGain();
          silentGain.gain.value = 0;
          this.source.connect(scriptNode);
          this.source.connect(silentGain);
          silentGain.connect(this.audioContext.destination);
        }
        this.recording = true;
        resolve();
      } catch (err) {
        this.recording = false;
        this.emit("error", err as any);
        reject(err);
      } finally {
        this.starting = null;
      }
    });
  }

  stop() {
    // its plausible that stop would be called before start completes
    // such as if the websocket immediately hangs up
    const handleStop = () => {
      this.source?.disconnect();
      this.stream?.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
      this.recordingWorklet = undefined;
      this.vuWorklet = undefined;
    };
    if (this.starting) {
      this.starting.then(handleStop);
      return;
    }
    handleStop();
  }
}
